import React, { useState, useEffect, useContext } from 'react';
import { School } from '../../types';
import { AppContext } from '../../App';
import { api } from '../../utils/api';

interface AuthPageProps {
  initialView: 'login' | 'register' | 'forgotPassword';
  onClose: () => void;
  invitationToken?: string | null;
}

const PasswordStrengthIndicator: React.FC<{ password?: string }> = ({ password = "" }) => {
    const checks = {
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /[0-9]/.test(password),
        special: /[!@#$%^&*?]/.test(password),
    };

    const CheckItem: React.FC<{ text: string; checked: boolean }> = ({ text, checked }) => (
        <li className={`flex items-center transition-colors ${checked ? 'text-green-600' : 'text-gray-500'}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {checked ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                )}
            </svg>
            <span className="text-sm">{text}</span>
        </li>
    );

    return (
        <ul className="space-y-1 mt-2">
            <CheckItem text="At least 8 characters" checked={checks.length} />
            <CheckItem text="An uppercase letter (A-Z)" checked={checks.uppercase} />
            <CheckItem text="A lowercase letter (a-z)" checked={checks.lowercase} />
            <CheckItem text="A number (0-9)" checked={checks.number} />
            <CheckItem text="A special character (!@#$%^&*?)" checked={checks.special} />
        </ul>
    );
};


const AuthPage: React.FC<AuthPageProps> = ({ initialView, onClose, invitationToken }) => {
  const appContext = useContext(AppContext);
  const [view, setView] = useState<'login' | 'register' | 'forgotPassword'>('login');
  const [authIdentifier, setAuthIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<'teacher' | 'parent'>('teacher');
  const [schoolId, setSchoolId] = useState("");
  const [schools, setSchools] = useState<School[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [formComplete, setFormComplete] = useState(false);

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const data = await api.get<School[]>('/api/v1/schools');
        data.sort((a, b) => a.school_name.localeCompare(b.school_name));
        setSchools(data);
        if (data.length > 0) setSchoolId(data[0].school_id);
      } catch (e) {
        if (e instanceof Error) setError("Could not load school list.");
      }
    };
    fetchSchools();
  }, []);
  
  useEffect(() => {
    setView(initialView);
    setError(null);
    setFormComplete(false);
    if (invitationToken) {
        setRole('parent');
    }
  }, [initialView, invitationToken]);

  const handleLoginSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setLoading(true);
  setError(null);

  if (!appContext) {
      setError("Application context is not available.");
      setLoading(false);
      return;
  }
  try {
    const isEmail = (input: string) => /\S+@\S+\.\S+/.test(input);
    const payload: { password: string; email?: string; username?: string } = { password };
    if (isEmail(authIdentifier)) payload.email = authIdentifier;
    else payload.username = authIdentifier;

    const data = await api.post<any>('/api/v1/auth/login', payload);

    if (data.mfa_required) {
        appContext.promptMfaVerify(data.mfa_token);
    } else {
        appContext.handleLogin(data.token, data.user);
    }
    onClose();
} catch (err) {
    if (err instanceof Error) setError(err.message);
    else setError('An unknown error occurred.');
} finally {
    setLoading(false);
}
};

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
        await api.post('/api/v1/auth/register', { 
            email, 
            password, 
            first_name: firstName, 
            last_name: lastName, 
            school_id: schoolId, 
            role,
            invitation_token: invitationToken
        });
        setFormComplete(true);
    } catch (err) {
        if (err instanceof Error) setError(err.message);
        else setError('An unknown error occurred.');
    } finally {
        setLoading(false);
    }
  }

  const renderContent = () => {
    if(formComplete) {
        const messages = {
            register: { title: "Registration Successful!", text: "If you registered as a teacher, an administrator will review and approve your account shortly. Parents can log in immediately.", button: "Back to Login", nextView: 'login' },
            register_parent_invite: { title: "Account Created & Linked!", text: "You have been successfully linked to your child's account. You can now log in to view their progress.", button: "Back to Login", nextView: 'login' },
        };
        const messageKey = view === 'register' && invitationToken ? 'register_parent_invite' : 'register';
        const message = messages[messageKey];

        return (
            <div className="text-center p-4">
                <h3 className="text-xl font-bold text-green-800">{message.title}</h3>
                <p className="text-gray-700 mt-2">{message.text}</p>
                <button onClick={() => { setView(message.nextView as any); setFormComplete(false); }} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md">{message.button}</button>
            </div>
        );
    }

    switch(view) {
        case 'register':
            return (
                <form onSubmit={handleRegisterSubmit}>
                    {error && <p className="text-red-500 text-center mb-4">{error}</p>}
                    {invitationToken && (
                        <div className="p-3 mb-4 bg-blue-100 border-l-4 border-blue-500 text-blue-700">
                            <p className="font-bold">Welcome, Parent!</p>
                            <p className="text-sm">You are registering with an invitation to view a student's progress.</p>
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700" htmlFor="firstName">First Name</label>
                            <input id="firstName" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700" htmlFor="lastName">Last Name</label>
                            <input id="lastName" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" required />
                        </div>
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700" htmlFor="school">School</label>
                        <select id="school" value={schoolId} onChange={(e) => setSchoolId(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md bg-white" required>
                            <option value="" disabled>Please select your school...</option>
                            {schools.map(school => <option key={school.school_id} value={school.school_id}>{school.school_name}</option>)}
                        </select>
                    </div>
                     <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700" htmlFor="role">Your Role</label>
                        <select id="role" value={role} onChange={(e) => setRole(e.target.value as 'teacher' | 'parent')} className="mt-1 block w-full p-2 border border-gray-300 rounded-md bg-white" required disabled={!!invitationToken}>
                            <option value="teacher">Teacher</option>
                            <option value="parent">Parent</option>
                        </select>
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700" htmlFor="email">Email</label>
                        <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" required />
                    </div>
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700" htmlFor="password">Password</label>
                        <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" required />
                        <PasswordStrengthIndicator password={password} />
                    </div>
                    <button type="submit" className="w-full py-2 px-4 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700" disabled={loading}>{loading ? "Registering..." : "Register"}</button>
                </form>
            );
        case 'forgotPassword':
            return (
                <div>
                    <p className="text-gray-600 text-center mb-4">This app doesn't send password-reset emails automatically. Here's who can reset your password:</p>
                    <ul className="space-y-3 mb-6">
                        <li className="p-3 bg-gray-50 rounded-md border border-gray-200">
                            <span className="font-semibold text-gray-800">Students</span>
                            <p className="text-sm text-gray-600 mt-0.5">Ask your teacher -- they can reset your password from their dashboard.</p>
                        </li>
                        <li className="p-3 bg-gray-50 rounded-md border border-gray-200">
                            <span className="font-semibold text-gray-800">Teachers &amp; Parents</span>
                            <p className="text-sm text-gray-600 mt-0.5">Ask your school administrator to reset your password.</p>
                        </li>
                        <li className="p-3 bg-gray-50 rounded-md border border-gray-200">
                            <span className="font-semibold text-gray-800">School Administrators</span>
                            <p className="text-sm text-gray-600 mt-0.5">Ask your platform (super) administrator to reset your password.</p>
                        </li>
                    </ul>
                    <button type="button" onClick={() => setView('login')} className="w-full py-2 px-4 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700">Back to Login</button>
                </div>
            );
        case 'login':
        default:
            return (
                <form onSubmit={handleLoginSubmit}>
                    {error && <p className="text-red-500 text-center mb-4">{error}</p>}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700" htmlFor="authIdentifier">Email or Username</label>
                        <input id="authIdentifier" type="text" value={authIdentifier} onChange={(e) => setAuthIdentifier(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" required />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700" htmlFor="password">Password</label>
                        <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" required />
                    </div>
                    <div className="text-right mb-6">
                        <button type="button" onClick={() => setView('forgotPassword')} className="text-sm text-blue-600 hover:underline">Forgot Password?</button>
                    </div>
                    <button type="submit" className="w-full py-2 px-4 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700" disabled={loading}>{loading ? "Loading..." : "Login"}</button>
                </form>
            );
    }
  };

  const titles = { login: 'Welcome Back', register: 'Create an Account', forgotPassword: 'Reset Your Password' };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-lg relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={onClose} className="absolute top-2 right-4 text-gray-500 hover:text-gray-800 text-3xl font-bold">&times;</button>
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">{titles[view]}</h2>
            {renderContent()}
             {!formComplete && (
                <p className="text-center text-gray-600 text-sm mt-6">
                    {view === 'login' || view === 'forgotPassword' ? "Don't have an account?" : "Already have an account?"}
                    <button onClick={() => setView(view === 'login' || view === 'forgotPassword' ? 'register' : 'login')} className="text-blue-600 hover:underline ml-1">
                    {view === 'login' || view === 'forgotPassword' ? "Register" : "Login"}
                    </button>
                </p>
             )}
        </div>
    </div>
  );
};

export default AuthPage;